import { actionContext, ApplicationActions } from "../domain/engine";
import type { ActionResult } from "../domain/types";
import { REQUIRED_WEBMCP_TOOL_NAMES, WEBMCP_INPUT_SCHEMAS, WEBMCP_RUNTIME_SCHEMAS, type RequiredWebMcpToolName } from "./schemas";
import type { WebMcpToolDefinition, WebMcpToolResult } from "./types";

const controllers = new WeakMap<Document, AbortController>();

function control(input: { request_id: string; expected_state_version?: number }) {
  return { requestId: input.request_id, ...(input.expected_state_version !== undefined ? { expectedStateVersion: input.expected_state_version } : {}) };
}

function result<T>(action: ActionResult<T>, data?: unknown): WebMcpToolResult {
  if (!action.ok) {
    const summary = action.error.message.slice(0, 240);
    return {
      content: [{ type: "text", text: summary }],
      structuredContent: { ok: false, state_version: action.stateVersion, changed: false, summary, error: { code: action.error.code, message: summary } },
      isError: true,
    };
  }
  const summary = action.announcement.slice(0, 240);
  return {
    content: [{ type: "text", text: summary }],
    structuredContent: { ok: true, state_version: action.stateVersion, changed: action.changed, summary, ...(data !== undefined ? { data } : {}), ...(action.undoToken ? { undo_token: action.undoToken } : {}) },
  };
}

function invalidToolInput(message: string, stateVersion: number): WebMcpToolResult {
  const summary = message.slice(0, 240);
  return { content: [{ type: "text", text: summary }], structuredContent: { ok: false, state_version: stateVersion, changed: false, summary, error: { code: "VALIDATION_ERROR", message: summary } }, isError: true };
}

const descriptions: Readonly<Record<RequiredWebMcpToolName, string>> = {
  apply_ui_preferences: "Apply a bounded set of approved interface preferences to the visible Productist marketplace. Use only safe finite values; the page shows provenance and undo.",
  apply_recent_shopping_interests: "Apply one to six bounded, non-sensitive recent shopping interests that the user approved sharing. The visible home adapts and each interest stays editable.",
  get_marketplace_state: "Read Productist's current bounded marketplace state, including human corrections. This does not read agent memory or change page state.",
  update_preference: "Add or correct one bounded product preference in Productist. Agent calls cannot overwrite a user-owned record.",
  reset_personalization: "Reset one interest, one preference, interface settings, the session, or request visible confirmation for all local data.",
  search_products: "Search the live Shopify Global Catalog and show validated results in Productist. Use supported structured filters; organic order never uses commission.",
  compare_products: "Compare two to four currently known products by explicit criteria and open the visible comparison. This does not buy or save products.",
  explain_product: "Open Productist's evidence view for a currently known product, separating merchant facts, catalog inference, Productist checks, unknowns, and compromises.",
  create_shopping_collection: "Build one bounded multi-category shopping mission with up to eight roles. It can leave explicit gaps and stops before human-reviewed merchant checkout.",
};

function annotations(name: RequiredWebMcpToolName) {
  const readOnlyHint = name === "get_marketplace_state" || name === "compare_products" || name === "explain_product";
  const untrustedContentHint = name === "search_products" || name === "compare_products" || name === "explain_product" || name === "create_shopping_collection";
  return { readOnlyHint, untrustedContentHint };
}

function definitions(actions: ApplicationActions): readonly WebMcpToolDefinition[] {
  return [
    {
      name: "apply_ui_preferences",
      description: descriptions.apply_ui_preferences,
      inputSchema: WEBMCP_INPUT_SCHEMAS.apply_ui_preferences,
      annotations: annotations("apply_ui_preferences"),
      async execute(raw) {
        const parsed = WEBMCP_RUNTIME_SCHEMAS.apply_ui_preferences.safeParse(raw);
        if (!parsed.success) return invalidToolInput(parsed.error.issues[0]?.message ?? "Invalid interface preferences.", actions.getState().stateVersion);
        const input = parsed.data;
        const prefs = input.preferences;
        const action = await actions.applyUiPreferences({
          control: control(input),
          preferences: {
            ...(prefs.density ? { density: prefs.density } : {}),
            ...(prefs.content_balance ? { contentBalance: prefs.content_balance } : {}),
            ...(prefs.comparison_style ? { comparisonStyle: prefs.comparison_style } : {}),
            ...(prefs.theme ? { theme: prefs.theme } : {}),
            ...(prefs.motion ? { motion: prefs.motion } : {}),
            ...(prefs.prioritized_facts ? { prioritizedFacts: prefs.prioritized_facts } : {}),
          },
          provenance: { source: input.source, scope: input.scope ?? "session", reason: input.reason, ...(input.confidence !== undefined ? { confidence: input.confidence } : {}) },
        }, actionContext("webmcp"));
        return result(action, action.ok ? { changed_fields: action.value.changedFields } : undefined);
      },
    },
    {
      name: "apply_recent_shopping_interests",
      description: descriptions.apply_recent_shopping_interests,
      inputSchema: WEBMCP_INPUT_SCHEMAS.apply_recent_shopping_interests,
      annotations: annotations("apply_recent_shopping_interests"),
      async execute(raw) {
        const parsed = WEBMCP_RUNTIME_SCHEMAS.apply_recent_shopping_interests.safeParse(raw);
        if (!parsed.success) return invalidToolInput(parsed.error.issues[0]?.message ?? "Invalid recent interests.", actions.getState().stateVersion);
        const input = parsed.data;
        const action = await actions.applyRecentShoppingInterests({
          control: control(input), source: input.source, interests: input.interests.map((interest) => ({
            label: interest.label, query: interest.query,
            ...(interest.department ? { department: interest.department } : {}),
            ...(interest.attributes ? { attributes: interest.attributes } : {}),
            strength: interest.strength, recency: interest.recency,
            ...(interest.budget ? { budget: interest.budget } : {}),
            ...(interest.destination_country ? { destinationCountry: interest.destination_country } : {}),
            reason: interest.reason,
            ...(interest.scope ? { scope: interest.scope } : {}),
            ...(interest.confidence !== undefined ? { confidence: interest.confidence } : {}),
          })),
        }, actionContext("webmcp"));
        return result(action, action.ok ? { interest_ids: action.value.interestIds } : undefined);
      },
    },
    {
      name: "get_marketplace_state",
      description: descriptions.get_marketplace_state,
      inputSchema: WEBMCP_INPUT_SCHEMAS.get_marketplace_state,
      annotations: annotations("get_marketplace_state"),
      async execute(raw) {
        const parsed = WEBMCP_RUNTIME_SCHEMAS.get_marketplace_state.safeParse(raw);
        if (!parsed.success) return invalidToolInput("get_marketplace_state accepts an empty object only.", actions.getState().stateVersion);
        const action = await actions.getMarketplaceState();
        return result(action, action.ok ? action.value : undefined);
      },
    },
    {
      name: "update_preference",
      description: descriptions.update_preference,
      inputSchema: WEBMCP_INPUT_SCHEMAS.update_preference,
      annotations: annotations("update_preference"),
      async execute(raw) {
        const parsed = WEBMCP_RUNTIME_SCHEMAS.update_preference.safeParse(raw);
        if (!parsed.success) return invalidToolInput(parsed.error.issues[0]?.message ?? "Invalid preference.", actions.getState().stateVersion);
        const input = parsed.data;
        const action = await actions.updatePreference({ control: control(input), ...(input.target_preference_id ? { targetPreferenceId: input.target_preference_id } : {}), rule: input.preference, provenance: { source: input.source, scope: input.scope ?? "session", reason: input.reason, ...(input.confidence !== undefined ? { confidence: input.confidence } : {}) } }, actionContext("webmcp"));
        return result(action, action.ok ? { preference_id: action.value.id, field: action.value.rule.field } : undefined);
      },
    },
    {
      name: "reset_personalization",
      description: descriptions.reset_personalization,
      inputSchema: WEBMCP_INPUT_SCHEMAS.reset_personalization,
      annotations: annotations("reset_personalization"),
      async execute(raw) {
        const parsed = WEBMCP_RUNTIME_SCHEMAS.reset_personalization.safeParse(raw);
        if (!parsed.success) return invalidToolInput(parsed.error.issues[0]?.message ?? "Invalid reset target.", actions.getState().stateVersion);
        const input = parsed.data;
        const target = input.target.kind === "interest" ? { kind: "interest" as const, interestId: input.target.interest_id }
          : input.target.kind === "preference" ? { kind: "preference" as const, preferenceId: input.target.preference_id }
            : input.target.kind === "ui_preferences" ? { kind: "ui_preferences" as const, scope: input.target.scope }
              : { kind: input.target.kind } as { kind: "session" } | { kind: "all_local" };
        const action = await actions.resetPersonalization({ control: control(input), target, reason: input.reason }, actionContext("webmcp"));
        return result(action, action.ok ? { status: action.value.status, target: action.value.target.kind } : undefined);
      },
    },
    {
      name: "search_products",
      description: descriptions.search_products,
      inputSchema: WEBMCP_INPUT_SCHEMAS.search_products,
      annotations: annotations("search_products"),
      async execute(raw, context) {
        const parsed = WEBMCP_RUNTIME_SCHEMAS.search_products.safeParse(raw);
        if (!parsed.success) return invalidToolInput(parsed.error.issues[0]?.message ?? "Invalid search.", actions.getState().stateVersion);
        const input = parsed.data;
        const filters = input.filters;
        const action = await actions.searchProducts({
          ...control(input), query: input.query,
          ...(input.context ? { context: { ...(input.context.address_country ? { addressCountry: input.context.address_country } : {}), ...(input.context.language ? { language: input.context.language } : {}), ...(input.context.currency ? { currency: input.context.currency } : {}), ...(input.context.intent ? { intent: input.context.intent } : {}) } } : {}),
          ...(filters ? { filters: { ...(filters.category_ids ? { categoryIds: filters.category_ids } : {}), ...(filters.price ? { price: filters.price } : {}), ...(filters.available !== undefined ? { available: filters.available } : {}), ...(filters.shop_ids ? { shopIds: filters.shop_ids } : {}), ...(filters.condition ? { condition: filters.condition } : {}), ...(filters.ships_to_country ? { shipsToCountry: filters.ships_to_country } : {}), ...(filters.ships_from_countries ? { shipsFromCountries: filters.ships_from_countries } : {}), ...(filters.attributes ? { attributes: filters.attributes } : {}), ...(filters.rating ? { rating: { ...(filters.rating.min !== undefined ? { min: filters.rating.min } : {}), ...(filters.rating.min_count !== undefined ? { minCount: filters.rating.min_count } : {}) } } : {}), ...(filters.price_tiers ? { priceTiers: filters.price_tiers } : {}) } } : {}),
          ...(input.sort ? { sort: input.sort } : {}), ...(input.page ? { page: input.page } : {}),
        }, actionContext("webmcp", context?.signal));
        const state = action.ok && (action.value.status === "success" || action.value.status === "empty") ? action.value.data : undefined;
        return result(action, state ? { query: state.query, product_ids: state.products.slice(0, 24).map((product) => product.id), count: state.products.length, total_count: state.totalCount, has_next_page: state.hasNextPage, data_source: state.dataSource } : action.ok ? { status: action.value.status } : undefined);
      },
    },
    {
      name: "compare_products",
      description: descriptions.compare_products,
      inputSchema: WEBMCP_INPUT_SCHEMAS.compare_products,
      annotations: annotations("compare_products"),
      async execute(raw) {
        const parsed = WEBMCP_RUNTIME_SCHEMAS.compare_products.safeParse(raw);
        if (!parsed.success) return invalidToolInput(parsed.error.issues[0]?.message ?? "Invalid comparison.", actions.getState().stateVersion);
        const input = parsed.data;
        const action = await actions.compareProducts({ control: control(input), candidates: input.candidates.map((candidate) => ({ productId: candidate.product_id, ...(candidate.variant_id ? { variantId: candidate.variant_id } : {}) })), criteria: input.criteria }, actionContext("webmcp"));
        return result(action, action.ok ? { comparison_id: action.value.id, product_ids: action.value.candidates.map((candidate) => candidate.productId), criteria: action.value.criteria } : undefined);
      },
    },
    {
      name: "explain_product",
      description: descriptions.explain_product,
      inputSchema: WEBMCP_INPUT_SCHEMAS.explain_product,
      annotations: annotations("explain_product"),
      async execute(raw) {
        const parsed = WEBMCP_RUNTIME_SCHEMAS.explain_product.safeParse(raw);
        if (!parsed.success) return invalidToolInput(parsed.error.issues[0]?.message ?? "Invalid product explanation request.", actions.getState().stateVersion);
        const input = parsed.data;
        const action = await actions.explainProduct({ control: control(input), candidate: { productId: input.product_id, ...(input.variant_id ? { variantId: input.variant_id } : {}) }, ...(input.focus ? { focus: input.focus } : {}) }, actionContext("webmcp"));
        return result(action, action.ok ? { product_id: action.value.candidate.productId, merchant_fact_count: action.value.merchantFacts.length, catalog_inference_count: action.value.catalogInferences.length, productist_check_count: action.value.productistChecks.length, unknown_count: action.value.unknowns.length, compromise_count: action.value.compromises.length } : undefined);
      },
    },
    {
      name: "create_shopping_collection",
      description: descriptions.create_shopping_collection,
      inputSchema: WEBMCP_INPUT_SCHEMAS.create_shopping_collection,
      annotations: annotations("create_shopping_collection"),
      async execute(raw, context) {
        const parsed = WEBMCP_RUNTIME_SCHEMAS.create_shopping_collection.safeParse(raw);
        if (!parsed.success) return invalidToolInput(parsed.error.issues[0]?.message ?? "Invalid shopping collection.", actions.getState().stateVersion);
        const input = parsed.data;
        const action = await actions.createShoppingCollection({ control: control(input), name: input.name, ...(input.budget ? { budget: input.budget } : {}), ...(input.destination_country ? { destinationCountry: input.destination_country } : {}), roles: input.roles.map((role) => ({ label: role.label, query: role.query, ...(role.department ? { department: role.department } : {}), required: role.required, targetQuantity: role.target_quantity ?? 1 })), ...(input.seed_items ? { seedItems: input.seed_items.map((seed) => ({ roleLabel: seed.role_label, productId: seed.product_id, ...(seed.variant_id ? { variantId: seed.variant_id } : {}), ...(seed.quantity ? { quantity: seed.quantity } : {}) })) } : {}), replaceExisting: input.replace_existing ?? false }, actionContext("webmcp", context?.signal));
        return result(action, action.ok ? { collection_id: action.value.collection.id, item_count: action.value.collection.items.length, missing_role_ids: action.value.assessment.missingRequiredRoleIds, warning_codes: action.value.assessment.warnings.map((warning) => warning.code) } : undefined);
      },
    },
  ];
}

export function registerProductistTools(documentRef: Document, actions: ApplicationActions): () => void {
  controllers.get(documentRef)?.abort();
  const modelContext = documentRef.modelContext;
  if (!modelContext) {
    actions.setWebMcpAvailable(false);
    return () => undefined;
  }
  const controller = new AbortController();
  controllers.set(documentRef, controller);
  for (const definition of definitions(actions)) void modelContext.registerTool(definition, { signal: controller.signal });
  actions.setWebMcpAvailable(true);
  return () => {
    controller.abort();
    if (controllers.get(documentRef) === controller) controllers.delete(documentRef);
    actions.setWebMcpAvailable(false);
  };
}

export function requiredToolNames(): readonly string[] { return REQUIRED_WEBMCP_TOOL_NAMES; }
